/**
 * PlacesAutocomplete Component
 *
 * Address input with Google Places autocomplete suggestions.
 * Uses the Place Autocomplete Data API for full UI control.
 * Safe for both provider/clinic and patient addresses — only the typed
 * street/city query is sent to Google; no patient identifiers are included.
 * @agentic connections
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Combobox, InputBase, Loader, Text, useCombobox } from "@mantine/core";
import { IconMapPin } from "@tabler/icons-react";
import { loadGoogleMapsApi } from "@/utils/google-maps/loader";
import styles from "./PlacesAutocomplete.module.css";

/**
 * Boston metro center for location biasing (Google Places API max radius 50km).
 * @agentic connections
 */
const LOCATION_BIAS: google.maps.CircleLiteral = {
  center: { lat: 42.3601, lng: -71.0589 }, // Boston
  radius: 50000, // Max allowed by API (50km / ~31 miles)
};

/**
 * Minimum characters before fetching suggestions.
 * @agentic connections
 */
const MIN_SEARCH_LENGTH = 3;

/**
 * Debounce delay for API calls (ms).
 * @agentic connections
 */
const DEBOUNCE_MS = 600;

/**
 * Structured address result from Google Places API.
 * @agentic connections
 */
export type PlaceResult = {
  streetNumber?: string;
  route?: string;
  street?: string;
  line2?: string;
  city?: string;
  state?: string;
  stateCode?: string;
  postalCode?: string;
  country?: string;
  countryCode?: string;
  latitude: number;
  longitude: number;
  placeId: string;
  formattedAddress: string;
};

/**
 * Internal suggestion type for autocomplete dropdown.
 * @agentic connections
 */
type Suggestion = {
  placeId: string;
  mainText: string;
  secondaryText: string;
  prediction: google.maps.places.PlacePrediction;
};

/**
 * Props for PlacesAutocomplete component.
 * @agentic connections
 */
type PlacesAutocompleteProps = {
  value?: string;
  onChange?: (value: string) => void;
  onPlaceSelect?: (place: PlaceResult) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string | boolean;
  label?: string;
  required?: boolean;
};

/**
 * Address input with Google Places autocomplete for provider/clinic locations.
 * @agentic connections
 */
export const PlacesAutocomplete = ({
  value = "",
  onChange,
  onPlaceSelect,
  placeholder = "Search for an address...",
  disabled = false,
  error,
  label,
  required,
}: PlacesAutocompleteProps) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Store combobox in ref to avoid dependency issues
  const comboboxRef = useRef(combobox);

  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [isApiAvailable, setIsApiAvailable] = useState<boolean | null>(null);

  // Session token for billing optimization
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  // Track latest request to avoid race conditions
  const requestIdRef = useRef(0);

  // Debounce timer ref
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flag to skip fetching after a selection
  const skipNextFetchRef = useRef(false);

  // Track the last query we fetched
  const lastFetchedQueryRef = useRef("");

  // Store callbacks in refs to avoid dependency issues
  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);

  useEffect(() => {
    comboboxRef.current = combobox;
    onChangeRef.current = onChange;
    onPlaceSelectRef.current = onPlaceSelect;
  });

  // Initialize API (runs once)
  useEffect(() => {
    let mounted = true;
    loadGoogleMapsApi().then((api) => {
      if (!mounted) return;
      setIsApiAvailable(!!api);
      if (api) {
        sessionTokenRef.current =
          new google.maps.places.AutocompleteSessionToken();
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  // Sync external value changes (defer setState to avoid cascading renders in effect)
  useEffect(() => {
    queueMicrotask(() => {
      setInputValue((prev) => {
        if (value === prev) {
          return prev;
        }
        skipNextFetchRef.current = true;
        return value;
      });
    });
  }, [value]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Core fetch function - no React dependencies, uses refs only
  const doFetch = useCallback(async (query: string) => {
    if (query === lastFetchedQueryRef.current) {
      setIsLoading(false);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    lastFetchedQueryRef.current = query;

    try {
      const request: google.maps.places.AutocompleteRequest = {
        input: query,
        sessionToken: sessionTokenRef.current ?? undefined,
        includedPrimaryTypes: ["street_address", "subpremise", "premise"],
        includedRegionCodes: ["us"],
        locationBias: LOCATION_BIAS,
        language: "en-US",
        region: "us",
      };

      const { suggestions: results } =
        await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          request,
        );

      // Ignore stale responses
      if (currentRequestId !== requestIdRef.current) return;

      const parsed: Suggestion[] = results
        .filter(
          (
            s,
          ): s is google.maps.places.AutocompleteSuggestion & {
            placePrediction: google.maps.places.PlacePrediction;
          } => s.placePrediction !== null,
        )
        .map((s) => {
          const pred = s.placePrediction;
          return {
            placeId: pred.placeId,
            mainText: pred.mainText?.text ?? pred.text.text,
            secondaryText: pred.secondaryText?.text ?? "",
            prediction: pred,
          };
        });

      setSuggestions(parsed);
      if (parsed.length > 0) {
        comboboxRef.current.openDropdown();
      }
    } catch (err) {
      console.error("[PlacesAutocomplete] Fetch error:", err);
      setSuggestions([]);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []); // No dependencies - uses refs only

  // Handle input changes with debounce
  const handleInputChange = useCallback(
    (newValue: string) => {
      setInputValue(newValue);
      onChangeRef.current?.(newValue);

      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      // Clear the skip flag since user is typing
      skipNextFetchRef.current = false;

      // Clear suggestions if too short
      if (newValue.length < MIN_SEARCH_LENGTH) {
        setSuggestions([]);
        setIsLoading(false);
        comboboxRef.current.closeDropdown();
        lastFetchedQueryRef.current = "";
        return;
      }

      // Skip if API not available
      if (!isApiAvailable) {
        return;
      }

      // Show loading indicator
      setIsLoading(true);

      // Schedule the fetch
      debounceTimerRef.current = setTimeout(() => {
        if (skipNextFetchRef.current) {
          skipNextFetchRef.current = false;
          setIsLoading(false);
          return;
        }
        doFetch(newValue);
      }, DEBOUNCE_MS);
    },
    [isApiAvailable, doFetch],
  );

  // Handle place selection
  const handleSelect = useCallback(async (suggestion: Suggestion) => {
    // Set skip flag FIRST
    skipNextFetchRef.current = true;

    // Cancel pending fetch
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    setIsLoading(false);
    setIsFetchingDetails(true);
    comboboxRef.current.closeDropdown();
    setSuggestions([]);

    try {
      const place = suggestion.prediction.toPlace();
      await place.fetchFields({
        fields: ["addressComponents", "formattedAddress", "location"],
      });

      const result = parsePlaceToResult(place);
      if (result) {
        const displayValue = result.street || result.formattedAddress;
        lastFetchedQueryRef.current = displayValue;
        setInputValue(displayValue);
        onChangeRef.current?.(displayValue);
        onPlaceSelectRef.current?.(result);
      }

      // New session token for next search
      sessionTokenRef.current =
        new google.maps.places.AutocompleteSessionToken();
    } catch (err) {
      console.error("[PlacesAutocomplete] Selection error:", err);
    } finally {
      setIsFetchingDetails(false);
    }
  }, []);

  // Render options
  // Note: Selection is handled by Combobox's onOptionSubmit, not onClick on each option.
  // Using both would cause handleSelect to fire twice.
  const options = suggestions.map((suggestion) => (
    <Combobox.Option key={suggestion.placeId} value={suggestion.placeId}>
      <div>
        <Text size="sm" fw={500}>
          {suggestion.mainText}
        </Text>
        {suggestion.secondaryText && (
          <Text size="xs" c="dimmed">
            {suggestion.secondaryText}
          </Text>
        )}
      </div>
    </Combobox.Option>
  ));

  // Fallback when API unavailable
  if (isApiAvailable === false) {
    return (
      <InputBase
        label={label}
        required={required}
        error={error}
        value={inputValue}
        onChange={(e) => handleInputChange(e.currentTarget.value)}
        placeholder={placeholder}
        disabled={disabled}
        leftSection={<IconMapPin size={16} />}
        description="Address autocomplete unavailable"
        autoComplete="nope-nope-nope"
        data-1p-ignore
        data-lpignore="true"
        data-form-type="other"
      />
    );
  }

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(placeId) => {
        const suggestion = suggestions.find((s) => s.placeId === placeId);
        if (suggestion) handleSelect(suggestion);
      }}
    >
      <Combobox.Target>
        <InputBase
          label={label}
          required={required}
          error={error}
          value={inputValue}
          onChange={(e) => handleInputChange(e.currentTarget.value)}
          onClick={() => {
            if (suggestions.length > 0) combobox.openDropdown();
          }}
          onFocus={() => {
            if (suggestions.length > 0) combobox.openDropdown();
          }}
          onBlur={() => combobox.closeDropdown()}
          placeholder={placeholder}
          disabled={disabled || isFetchingDetails}
          leftSection={<IconMapPin size={16} />}
          rightSection={
            (isLoading || isFetchingDetails) && <Loader size="xs" />
          }
          rightSectionPointerEvents="none"
          // Aggressively disable browser autofill
          autoComplete="nope"
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          name="address-search-field"
          id="places-autocomplete-input"
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {options.length > 0 ? (
            options
          ) : isLoading ? (
            <Combobox.Empty>Searching...</Combobox.Empty>
          ) : (
            <Combobox.Empty>No results found</Combobox.Empty>
          )}
        </Combobox.Options>

        {/* Google attribution (required by ToS) */}
        <div className={styles.attribution}>
          <Text size="xs" c="dimmed">
            Powered by Google
          </Text>
        </div>
      </Combobox.Dropdown>
    </Combobox>
  );
};

/**
 * Parses Place object into structured PlaceResult format.
 * @agentic connections
 */
function parsePlaceToResult(
  place: google.maps.places.Place,
): PlaceResult | null {
  const location = place.location;
  const placeId = place.id;

  if (!location || !placeId) return null;

  let latitude: number;
  let longitude: number;

  const locationUnknown = location as unknown;
  if (typeof (locationUnknown as { lat?: () => number }).lat === "function") {
    const latLng = locationUnknown as google.maps.LatLng;
    latitude = latLng.lat();
    longitude = latLng.lng();
  } else if (typeof (locationUnknown as { lat?: number }).lat === "number") {
    const literal = locationUnknown as google.maps.LatLngLiteral;
    latitude = literal.lat;
    longitude = literal.lng;
  } else {
    return null;
  }

  const components = place.addressComponents ?? [];
  const getComponent = (type: string, useShort = false): string | undefined => {
    const comp = components.find((c) => c.types?.includes(type));
    if (!comp) return undefined;
    return useShort
      ? (comp.shortText ?? undefined)
      : (comp.longText ?? undefined);
  };

  const streetNumber = getComponent("street_number");
  const route = getComponent("route");
  const street = [streetNumber, route].filter(Boolean).join(" ") || undefined;

  return {
    streetNumber,
    route,
    street,
    city:
      getComponent("locality") ||
      getComponent("sublocality") ||
      getComponent("administrative_area_level_2"),
    state: getComponent("administrative_area_level_1"),
    stateCode: getComponent("administrative_area_level_1", true),
    postalCode: getComponent("postal_code"),
    country: getComponent("country"),
    countryCode: getComponent("country", true),
    latitude,
    longitude,
    placeId,
    formattedAddress: place.formattedAddress ?? "",
  };
}
